/**
 * Owner: Masa Bayu
 * File: author-discovery-data.ts
 * Date:Time: 2026-10-23 00:11
 * Description: §411 Author Discovery — projection di atas katalog nyata:
 *   penapis pil (All/Fiction/Non-Fiction/Most Titles), carian nama, Featured
 *   (paling banyak terbit), Terbaharu (tarikh terbit), kiraan author ikut kategori,
 *   buku terbitan terkini, dan author serupa (genre utama sama).
 *   §466 S1 — jumlah penilaian DAN kiraan pengikut dibuang: tiada endpoint
 *   audiens di hub, jadi susunan ikut kiraan yang boleh deriva daripada katalog
 *   sebenar (bilangan tajuk, kemudian tarikh terbit).
 */

import type {
  MarketplaceBook,
  MarketplaceCategoryId,
} from '../../home/marketplace/marketplace-projection';
import { byRecencyDesc, type PublicAuthor } from '../public/public-author-data';

export type AuthorPill = 'all' | 'fiction' | 'nonfiction' | 'popular';

export const AUTHOR_PILL_ORDER: readonly AuthorPill[] = Object.freeze([
  'all',
  'fiction',
  'nonfiction',
  'popular',
]);

/** Genre yang dikira "fiction" untuk pil penuras (taksonomi QX §405). */
const FICTION_GENRES: readonly MarketplaceCategoryId[] = Object.freeze([
  'fiction',
  'scifi-fantasy',
  'literature',
  'comics',
]);

export const RAIL_LIMIT = 5;
export const BOOKS_RAIL_LIMIT = 8;
export const CATEGORY_TILE_LIMIT = 6;
export const SIMILAR_LIMIT = 4;

export function isFictionAuthor(author: PublicAuthor): boolean {
  return author.genres.some((genre) => FICTION_GENRES.includes(genre));
}

export function authorMatchesPill(author: PublicAuthor, pill: AuthorPill): boolean {
  if (pill === 'all' || pill === 'popular') {
    return true;
  }
  return pill === 'fiction' ? isFictionAuthor(author) : !isFictionAuthor(author);
}

export function authorMatchesQuery(author: PublicAuthor, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }
  return author.name.toLowerCase().includes(needle);
}

/** Senarai author tertapis ikut pil + carian; "popular" = isih bilangan tajuk. */
export function filterAuthors(
  authors: readonly PublicAuthor[],
  pill: AuthorPill,
  query: string,
): readonly PublicAuthor[] {
  const filtered = authors.filter(
    (author) => authorMatchesPill(author, pill) && authorMatchesQuery(author, query),
  );
  if (pill === 'popular') {
    return [...filtered].sort(byTitles);
  }
  return filtered;
}

function byName(a: PublicAuthor, b: PublicAuthor): number {
  return a.name.localeCompare(b.name);
}

/** Tarikh terbit paling terkini milik author; null jika tiada buku bertarikh. */
function latestPublishedAt(author: PublicAuthor): string | null {
  let latest: string | null = null;
  for (const book of author.books) {
    if (book.publishedAt !== null && (latest === null || book.publishedAt > latest)) {
      latest = book.publishedAt;
    }
  }
  return latest;
}

/** Isih "terkemuka" yang benar: paling banyak tajuk, kemudian terbitan terkini. */
function byTitles(a: PublicAuthor, b: PublicAuthor): number {
  if (a.books.length !== b.books.length) return b.books.length - a.books.length;
  const left = latestPublishedAt(a);
  const right = latestPublishedAt(b);
  if (left !== null && right !== null && left !== right) return right.localeCompare(left);
  if (left !== null && right === null) return -1;
  if (left === null && right !== null) return 1;
  return byName(a, b);
}

/** Featured = author dengan paling banyak buku dalam katalog. */
export function featuredAuthors(
  authors: readonly PublicAuthor[],
  limit = RAIL_LIMIT,
): readonly PublicAuthor[] {
  return [...authors].sort(byTitles).slice(0, limit);
}

/** Terbaharu = author dengan terbitan paling terkini (tarikh hub yang sebenar). */
export function trendingAuthors(
  authors: readonly PublicAuthor[],
  limit = RAIL_LIMIT,
): readonly PublicAuthor[] {
  return [...authors]
    .sort((a, b) => {
      const left = latestPublishedAt(a);
      const right = latestPublishedAt(b);
      if (left !== null && right !== null && left !== right) return right.localeCompare(left);
      if (left !== null && right === null) return -1;
      if (left === null && right !== null) return 1;
      return byName(a, b);
    })
    .slice(0, limit);
}

/** Kiraan author unik ikut kategori utama (termasuk genre sekunder buku). */
export function categoryAuthorCounts(
  books: readonly MarketplaceBook[],
): ReadonlyMap<MarketplaceCategoryId, number> {
  const authorsByCategory = new Map<MarketplaceCategoryId, Set<string>>();
  for (const book of books) {
    // §466 S1 — manifest tanpa penulis/kategori tidak dikira (fail-closed).
    if (book.author === null || book.categoryId === null) continue;
    const ids: MarketplaceCategoryId[] = [book.categoryId, ...book.secondaryCategoryIds];
    for (const id of ids) {
      let set = authorsByCategory.get(id);
      if (set === undefined) {
        set = new Set<string>();
        authorsByCategory.set(id, set);
      }
      set.add(book.author);
    }
  }
  const counts = new Map<MarketplaceCategoryId, number>();
  for (const [id, set] of authorsByCategory) {
    counts.set(id, set.size);
  }
  return counts;
}

export interface PopularCategoryTile {
  readonly categoryId: MarketplaceCategoryId;
  readonly authorCount: number;
}

/** Top-N kategori ikut bilangan author (seri → urutan taksonomi). */
export function popularCategoryTiles(
  books: readonly MarketplaceBook[],
  categoryOrder: readonly MarketplaceCategoryId[],
  limit = CATEGORY_TILE_LIMIT,
): readonly PopularCategoryTile[] {
  const counts = categoryAuthorCounts(books);
  return categoryOrder
    .map((categoryId) => ({ categoryId, authorCount: counts.get(categoryId) ?? 0 }))
    .filter((tile) => tile.authorCount > 0)
    .sort((a, b) => b.authorCount - a.authorCount)
    .slice(0, limit);
}

/** Buku terbitan terkini (tarikh hub) — untuk karusel "by author". */
export function latestBooksByAuthor(
  books: readonly MarketplaceBook[],
  limit = BOOKS_RAIL_LIMIT,
): readonly MarketplaceBook[] {
  return [...books].sort(byRecencyDesc).slice(0, limit);
}

/** Author serupa = kongsi genre utama yang sama, ikut bilangan tajuk. */
export function similarAuthors(
  authors: readonly PublicAuthor[],
  target: PublicAuthor,
  limit = SIMILAR_LIMIT,
): readonly PublicAuthor[] {
  const primaryGenre = target.genres[0];
  if (primaryGenre === undefined) {
    return [];
  }
  return authors
    .filter((author) => author.id !== target.id && author.genres.includes(primaryGenre))
    .sort(byTitles)
    .slice(0, limit);
}
